import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DailyIframe from '@daily-co/daily-js';
import exchangeService from '../../services/exchangeService';
import ReportModal from './ReportModal';
import { FaSpinner, FaPhoneSlash, FaFlag, FaArrowLeft } from 'react-icons/fa';
import '../../CallRoom.css';

// 매칭 1개(matchId)에 대한 1:1 영상통화방이다.
// Daily.co가 만들어준 iframe을 이 화면에 통째로 붙여넣는 방식(embed)이라
// 우리가 직접 WebRTC를 다룰 필요는 없다. 백엔드가 미리 만들어준 roomUrl + token으로만
// 입장하기 때문에, 아무나 이 방에 들어올 수는 없다.
//
// 중요: 아래 return문은 status와 상관없이 항상 같은 레이아웃(헤더 + iframe이 들어갈
// call-room-frame-container + 컨트롤 바)을 렌더링한다. status에 따라 "연결 중.../에러/종료됨"은
// 그 위에 오버레이로만 얹는다. 예전에는 status별로 완전히 다른 return을 했었는데, 그러면
// "연결 중" 화면일 때 call-room-frame-container 자체가 DOM에 없어서 containerRef.current가
// null이 되고, DailyIframe.createFrame()이 우리가 만든 위치가 아니라 브라우저 화면 구석에
// 자기 멋대로 둥둥 뜨는(default floating) 프레임을 만들어버렸다. 항상 렌더링해야 이 문제가 없다.
const CallRoom = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();

  const callFrameRef = useRef(null);
  const containerRef = useRef(null);
  const callSessionIdRef = useRef(null);
  const endingRef = useRef(false); // 통화 종료 처리가 중복 실행되는 것을 막는 플래그
  // useEffect의 cleanup 함수는 effect가 처음 실행될 때의 상태값을 클로저로 가둬버려서,
  // state(status)를 직접 읽으면 항상 'starting'만 보게 된다(리렌더돼도 cleanup 안의 값은 안 바뀜).
  // 그래서 '지금 실제로 통화가 연결돼 있는가'는 이 ref로 따로 최신값을 유지한다.
  const joinedRef = useRef(false);
  // React.StrictMode(index.js)는 개발 모드에서 effect를 mount→cleanup→mount로 한 번 더
  // 실행한다. startCall처럼 비동기로 진행되는 부수효과(DailyIframe 생성 등)가 이 두 번째
  // 실행과 겹치면, 첫 번째 실행이 아직 진행 중인 상태에서 두 번째가 또 DailyIframe.createFrame()을
  // 호출해서 "Duplicate DailyIframe instances are not allowed" 에러가 난다.
  // runIdRef로 "지금 유효한 실행이 어떤 것인지"를 추적해서, 낡은(취소된) 실행이 뒤늦게 깨어나도
  // 더 진행하지 않고 자기가 만든 것(세션/프레임)을 정리하고 멈추게 한다.
  const runIdRef = useRef(0);

  const [status, setStatus] = useState('starting'); // starting | joined | ended | error
  const [errorMessage, setErrorMessage] = useState('');
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [partnerNickname, setPartnerNickname] = useState('');
  const [partnerUserId, setPartnerUserId] = useState(null);

  useEffect(() => {
    const myRunId = ++runIdRef.current;
    startCall(myRunId);
    // 화면을 떠날 때(뒤로가기, 새로고침 등) 통화가 켜진 채로 남아있지 않도록 정리한다.
    // StrictMode의 첫 번째 실행에 대해서는, 이 cleanup이 runIdRef를 이미 앞서가게 만들어서
    // startCall(myRunId) 쪽이 스스로 "내가 낡은 실행이다"를 감지하고 정리하게 만든다.
    return () => {
      cleanupCallFrame();
      if (joinedRef.current) {
        endCallOnServer();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  // 내가 통화방에 있는 동안(연결 시도 중 status === 'starting' 포함), 상대가 채팅방 배너에서
  // "끊기"를 누르거나 방맜 쪽(연결/통화 화면)에서 "취소/통화종료"를 누르면, 서버에서는 이미
  // 세션이 종료되고 Daily.co 방도 삭제된 상태다. 이 쪽 화면도 3초마다 확인해서 같이 닫아야
  // 한다 — 연결 중(starting)이든 연결됨(joined)이든 둘 다 해당된다.
  useEffect(() => {
    if (status !== 'joined' && status !== 'starting') return undefined;

    const intervalId = setInterval(async () => {
      try {
        const response = await exchangeService.getCallStatus(matchId);
        const active = Boolean(response.data?.active);
        // 상대(또는 나)가 끝내서 서버에서 이미 종료된 세션이라면, 내 화면도 같이 닫는다.
        // joined 상태에서는 joinedRef.current로 확인한다. starting 상태에서는 반드시
        // callSessionIdRef.current가 이미 채워져 있을 때만(=내 쪽 startCall이 세션을 만든 뒤일
        // 때만) "종료되다"로 판단한다 — 그렇지 않으면, 내 startCall 요청이 아직 서버에 도달하기도
        // 전인 아주 초반 타이밍에 이 폴링이 먼저 실행되면서 "아직 세션이 없다"를 "상대가
        // 취소했다"로 착각해 정상적인 통화 시작을 스스로 끓어버리는 오작동이 생긴다.
        if (!active && (joinedRef.current || (status === 'starting' && callSessionIdRef.current))) {
          runIdRef.current += 1; // 진행 중이던 startCall이 있다면 냑은 실행으로 만들어 멈추게 한다.
          joinedRef.current = false;
          cleanupCallFrame();
          setErrorMessage('상대가 통화를 종료(또는 취소)했다.');
          setStatus('ended');
        }
      } catch (err) {
        console.error('통화 상태 확인 오류:', err);
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [status, matchId]);

  // 통화 중(status === 'joined')일 때만, 화면에 보이는 "오늘 남은 통화 가능 시간"을
  // 1초마다 1씩 줄여서 실시간으로 줄어드는 게 눈에 보이게 한다.
  // 실제 사용시간 차감/제한 체크는 서버(ExchangeLimitService)에서 하므로, 이 타이머는
  // 어디까지나 화면 표시용이다. 0 밑으로는 내려가지 않게 막는다.
  useEffect(() => {
    if (status !== 'joined') return undefined;

    const intervalId = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev == null) return prev;
        const next = Math.max(0, prev - 1);
        // 0이 되는 순간 통화를 자동으로 종료한다. 서버에서도 어차피 제한 시간을 초과하면
        // 거부하지만, 화면에서도 사용자가 직접 끊기 전에 자연스럽게 종료되도록 한다.
        if (next === 0 && joinedRef.current) {
          joinedRef.current = false;
          cleanupCallFrame();
          endCallOnServer();
          setErrorMessage('오늘의 영상통화 가능 시간을 모두 사용했다.');
          setStatus('ended');
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [status]);

  const cleanupCallFrame = () => {
    if (callFrameRef.current) {
      callFrameRef.current.destroy();
      callFrameRef.current = null;
    }
  };

  const startCall = async (runId) => {
    // 이 실행이 여전히 "최신" 실행인지 매 단계마다 확인한다. runIdRef.current가 내가 시작할 때
    // 받은 값과 다르다면, 그 사이에 effect가 다시 실행돼서(StrictMode 이중 실행 또는 matchId 변경)
    // 내가 낡은 실행이 됐다는 뜻이다.
    const isStale = () => runIdRef.current !== runId;

    try {
      setStatus('starting');
      setErrorMessage('');

      // 상대 닉네임은 매칭 목록에서 찾아 화면 상단에 표시만 해준다(없어도 통화엔 지장 없음).
      try {
        const matchesRes = await exchangeService.getMyMatches();
        if (isStale()) return;
        const matchInfo = (matchesRes.data || []).find((m) => String(m.id) === String(matchId));
        if (matchInfo) {
          setPartnerNickname(matchInfo.partnerNickname);
          setPartnerUserId(matchInfo.partnerUserId);
        }
      } catch (e) {
        // 닉네임 조회 실패는 통화 자체를 막을 이유가 아니다.
      }

      if (isStale()) return;

      const response = await exchangeService.startCall(matchId);
      const { callSessionId, roomUrl, token, remainingSecondsToday } = response.data;

      if (isStale()) {
        // 이미 취소된 실행이라면, 방금 서버에 만들어진 세션을 바로 종료 처리해서
        // 일일 통화 한도가 낭비되거나 상대에게 유령 세션이 노출되지 않게 한다.
        exchangeService.endCall(callSessionId).catch(() => {});
        return;
      }

      callSessionIdRef.current = callSessionId;
      setRemainingSeconds(remainingSecondsToday);

      // 방어코드: 이전 테스트/버그로 이 브라우저 탭 안에 파기되지 않은 Daily 인스턴스가
      // 남아있을 수 있다. Daily는 탭(페이지) 하나에 통화 인스턴스가 단 하나만 있도록
      // 강제하므로, 만약 남아있는 게 있다면 새 프레임을 만들기 전에 먼저 파기해서
      // "Duplicate DailyIframe instances are not allowed" 오류를 방지한다.
      const existingCallObject = DailyIframe.getCallInstance();
      if (existingCallObject) {
        try {
          await existingCallObject.destroy();
        } catch (e) {
          console.error('기존 Daily 인스턴스 파기 오류:', e);
        }
      }

      if (isStale()) {
        exchangeService.endCall(callSessionId).catch(() => {});
        return;
      }

      // containerRef는 이제 status와 무관하게 항상 DOM에 존재하므로(아래 return문 참고),
      // 여기서 null일 일이 없다.
      const callFrame = DailyIframe.createFrame(containerRef.current, {
        showLeaveButton: false,
        iframeStyle: {
          width: '100%',
          height: '100%',
          border: '0',
        },
      });

      if (isStale()) {
        // 프레임을 만들자마자 낡은 실행임이 확인되면, 화면에 붙이지 않고 바로 파기한다.
        callFrame.destroy();
        return;
      }

      callFrameRef.current = callFrame;
      callFrame.on('left-meeting', handleLeaveMeeting);
      callFrame.on('error', handleCallError);

      await callFrame.join({ url: roomUrl, token });

      if (isStale()) {
        callFrame.destroy();
        callFrameRef.current = null;
        return;
      }

      joinedRef.current = true;
      setStatus('joined');
    } catch (err) {
      if (isStale()) return;
      console.error('통화 시작 오류:', err);
      // 에러 원인을 정확히 구분해서 보여준다:
      // 1) err.response가 있으면 서버까지 도달해서 에러 응답을 받은 것(400/500 등)
      // 2) err.request만 있고 err.response가 없으면 요청은 보냈는데 응답을 못 받은 것
      //    (백엔드가 꺼져있거나, CORS/네트워크 문제일 때 이렇다)
      // 3) 다른 자바스크립트 오류
      let displayMessage;
      if (err.response) {
        const serverMessage = err.response.data && err.response.data.message;
        displayMessage =
          serverMessage ||
          `서버 오류 (HTTP ${err.response.status}). 백엔드 콘솔 로그를 확인해달라.`;
      } else if (err.request) {
        displayMessage = '백엔드에 요청을 보냈지만 응답을 받지 못했다. 백엔드가 켜져있는지, 네트워크(CORS/방화벽) 문제가 있는지 확인해달라.';
      } else {
        displayMessage = `요청 자체가 실패했다: ${err.message || err.errorMsg || JSON.stringify(err) || '알 수 없는 오류'}`;
      }
      setErrorMessage(displayMessage);
      setStatus('error');
    }
  };

  const handleCallError = (event) => {
    console.error('Daily 통화 오류:', event);
  };

  const handleLeaveMeeting = useCallback(() => {
    handleEndCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const endCallOnServer = async () => {
    if (endingRef.current || !callSessionIdRef.current) return;
    endingRef.current = true;
    try {
      await exchangeService.endCall(callSessionIdRef.current);
    } catch (err) {
      console.error('통화 종료 기록 오류:', err);
    }
  };

  const handleEndCall = async () => {
    // 지금 진행 중인 startCall이 있다면(연결 시도 중에 사용자가 종료를 누른 경우), 그 실행을
    // "내가 냑은 실행이다"로 만들어서(isStale() 가 true가 되게) 더 이상 진행되지 않게 한다.
    // 이게 없으면, 사용자가 연결 중에 나가도 백그라운드의 startCall이 계속 진행돼서
    // 내가 나간 뒤에도 몰래 연결되고 status가 'joined'로 바뀌어버릴 수 있다.
    runIdRef.current += 1;
    joinedRef.current = false;
    cleanupCallFrame();
    await endCallOnServer();
    setStatus('ended');
  };

  const formatMinutes = (seconds) => {
    if (seconds == null) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}분 ${s}초`;
  };

  const showOverlay = status !== 'joined';
  const showControls = status === 'joined' || status === 'starting';
  // 뒤로가기/통화종료 버튼이 해야 할 일: 연결 중(starting)이나 이미 연결됨(joined)이면
  // 진행 중인 통화(시도)를 정리하면서 나가야 하고, error/ended일 때는 이미 정리가 끝난 상태이므로
  // 그냥 채팅방으로 이동만 하면 된다.
  const needsCleanupOnLeave = status === 'joined' || status === 'starting';

  return (
    <div className="call-room-container">
      <div className="call-room-header">
        <button
          className="call-room-back-button"
          onClick={needsCleanupOnLeave ? handleEndCall : () => navigate(`/exchange/chats/${matchId}`)}
        >
          <FaArrowLeft />
        </button>
        <span className="call-room-header-nickname">
          {status === 'joined'
            ? `${partnerNickname || '상대방'}과(와) 통화 중`
            : '영상통화'}
        </span>
        {remainingSeconds != null && status === 'joined' && (
          <span
            className={`call-room-remaining${remainingSeconds <= 60 ? ' call-room-remaining-low' : ''}`}
          >
            남은 시간 {formatMinutes(remainingSeconds)}
          </span>
        )}
        {status === 'joined' && (
          <button className="call-room-report-button" onClick={() => setShowReportModal(true)}>
            <FaFlag /> 신고
          </button>
        )}
      </div>

      {status === 'joined' && (
        <div className="call-room-notice">
          옷을 착용한 상태로 참여해달라. 부적절한 행동은 신고 대상이 된다.
        </div>
      )}

      {/* 이 div는 status와 무관하게 항상 DOM에 존재해야 한다. DailyIframe.createFrame()이
          이 요소를 컨테이너로 받아서 그 안에 iframe을 넣기 때문에, 로딩/에러 화면일 때도
          이 div 자체는 계속 있어야 하고, 대신 그 위를 오버레이로 덮어서 안 보이게 한다. */}
      <div className="call-room-frame-wrapper">
        <div className="call-room-frame-container" ref={containerRef} />

        {showOverlay && (
          <div className="call-room-overlay">
            {status === 'starting' && (
              <div className="exchange-loading">
                <FaSpinner className="exchange-spinner" />
                <p>통화 연결 중이다...</p>
              </div>
            )}

            {status === 'error' && (
              <div className="call-room-error-container">
                <p>{errorMessage}</p>
                <Link to={`/exchange/chats/${matchId}`}>채팅방으로 돌아가기</Link>
              </div>
            )}

            {status === 'ended' && (
              <div className="call-room-error-container">
                <p>{errorMessage || '통화가 종료되었다.'}</p>
                <Link to={`/exchange/chats/${matchId}`}>채팅방으로 돌아가기</Link>
              </div>
            )}
          </div>
        )}
      </div>

      {showControls && (
        <div className="call-room-controls">
          <button className="call-room-end-button" onClick={handleEndCall}>
            <FaPhoneSlash /> {status === 'joined' ? '통화 종료' : '연결 취소'}
          </button>
        </div>
      )}

      {showReportModal && (
        <ReportModal
          targetId={partnerUserId}
          targetNickname={partnerNickname}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </div>
  );
};

export default CallRoom;
